/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  HTTP_OPTION_MAILISEARCH_CTX,
  HTTP_OPTION_TOKEN_CTX_CONFIG,
} from '@/tokens';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  inject,
  Injectable,
  makeStateKey,
  PLATFORM_ID,
  TransferState,
} from '@angular/core';
import { StoreCartResponse } from '@medusajs/types';
import { environment } from '@/config/runtime-config';
import { catchError, map, Observable, of, tap } from 'rxjs';
import {
  CustomStoreProductListResponse,
  CustomStoreProductResponse,
} from '../models/product-item';
import { ProductRatingReview, ProductReview } from '../models/product-review';
import { GetInputParams, GetInputParamsMeiliSearch } from '@/models/params';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class ProductService {
  private http = inject(HttpClient);
  private transferState = inject(TransferState);
  private platformId = inject(PLATFORM_ID);

  private URL_API = environment.baseApiUrl + '/store';
  private MEILISEARCH_URL_API = environment.meilisearchApiUrl;
  readonly HTTP_OPTION_TOKEN_CTX = inject(HTTP_OPTION_TOKEN_CTX_CONFIG);

  getProductsList(
    params: GetInputParams
  ): Observable<CustomStoreProductListResponse> {
    const STATE_KEY = makeStateKey<CustomStoreProductListResponse>(
      'products_list_' + JSON.stringify(params)
    );

    if (this.transferState.hasKey(STATE_KEY)) {
      const cached = this.transferState.get(STATE_KEY, null);
      this.transferState.remove(STATE_KEY);
      return of(cached!);
    }
    const httpParams = new HttpParams(params.filters);
    return this.http
      .get<CustomStoreProductListResponse>(`${this.URL_API}${params.url}`, {
        params: httpParams,
      })
      .pipe(
        tap(data => {
          if (!isPlatformBrowser(this.platformId)) {
            this.transferState.set(STATE_KEY, data);
          }
        })
      );
  }

  addVariantToCart(
    variant_id: string,
    cartId: string,
    quantity = 1
  ): Observable<StoreCartResponse> {
    return this.http.post<StoreCartResponse>(
      `${this.URL_API}/carts/${cartId}/line-items`,
      {
        variant_id,
        quantity,
      }
    );
  }

  getPricesByProductId(
    params: GetInputParams
  ): Observable<CustomStoreProductResponse> {
    const httpParams = new HttpParams(params.filters);

    return this.http.get<CustomStoreProductResponse>(
      `${this.URL_API}${params.url}`,
      {
        params: httpParams,
      }
    );
  }

  getProductsListFromMeiliSearch(
    params: GetInputParamsMeiliSearch
  ): Observable<any> {
    return this.http.post<any>(
      `${this.MEILISEARCH_URL_API}/products/search`,
      {
        ...params.query,
        matchingStrategy: 'all',
      },
      {
        headers: { Authorization: `Bearer ${environment.meilisearchKey}` },
        context: HTTP_OPTION_MAILISEARCH_CTX,
      }
    );
  }

  getProductRatingReviewById(id: string) {
    return this.http
      .get<ProductRatingReview>(
        `${this.URL_API}/products/${id}/start-ratings/`,
        this.HTTP_OPTION_TOKEN_CTX
      )
      .pipe(
        catchError(() =>
          of({
            averageRating: 0,
            starBreakdown: [
              {
                star: 1,
                count: 0,
                percentage: 0,
              },
              {
                star: 2,
                count: 0,
                percentage: 0,
              },
              {
                star: 3,
                count: 0,
                percentage: 0,
              },
              {
                star: 4,
                count: 0,
                percentage: 0,
              },
              {
                star: 5,
                count: 0,
                percentage: 0,
              },
            ],
            totalReviews: 0,
          })
        )
      );
  }

  updateProductReview(
    data: Pick<
      ProductReview,
      'title' | 'content' | 'rating' | 'first_name' | 'last_name'
    >,
    id: string
  ) {
    return this.http
      .put<
        ProductReview[]
      >(`${this.URL_API}/reviews/${id}`, data, this.HTTP_OPTION_TOKEN_CTX)
      .pipe(map(data => data[0]));
  }

  createProductReview(
    data: Omit<ProductReview, 'created_at' | 'id' | 'status'>
  ) {
    return this.http
      .post<{
        review: ProductReview;
      }>(`${this.URL_API}/reviews/`, data, this.HTTP_OPTION_TOKEN_CTX)
      .pipe(map(data => data.review));
  }

  getProductsReviewsByOrderId(id: string) {
    return this.http.get<ProductReview[]>(
      `${this.URL_API}/custom-orders/${id}/reviews/`,
      this.HTTP_OPTION_TOKEN_CTX
    );
  }
}
